// src/components/dashboard/LessonCard.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const LessonCard = ({ lesson, isPurchased, isLocked }) => {
  const {
    id,
    title,
    price,
    duration,
    totalModules,
    coreModules,
    optionalModules,
    available,
    comingSoon,
    accreditation
  } = lesson;

  // Determine card state
  const canAccess = isPurchased || !isLocked;
  const showPrice = isLocked && available && !comingSoon;
  const buttonText = comingSoon 
    ? 'Binnenkort Beschikbaar' 
    : isPurchased 
      ? 'Ga verder met leren' 
      : 'Bekijk & Koop';

  // For purchased lessons, link to lesson dashboard (e.g., /les1)
  // For locked lessons, link to payment page (e.g., /payment/les1)
  // For coming soon, link is disabled via onClick preventDefault
  const buttonLink = comingSoon 
    ? '#' 
    : canAccess 
      ? `/les${id.replace('les', '')}` 
      : `/payment/${id}`;

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 ${
      canAccess ? 'hover:-translate-y-1 hover:shadow-lg' : 'opacity-90'
    }`}>
      <div className="p-6">
        {/* Header with Lock/Unlock Badge */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-2xl font-light text-purple-800 flex-1">
            {title}
          </h3>
          {isPurchased && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
              Gekocht
            </span>
          )}
          {isLocked && !comingSoon && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Vergrendeld
            </span>
          )}
          {comingSoon && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Binnenkort
            </span>
          )}
        </div>

        {/* Lesson Details */}
        <div className="space-y-2 mb-4 text-sm text-gray-600">
          {duration && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span><strong>Duur:</strong> {duration} minuten ({Math.round(duration / 60)} uur)</span>
            </div>
          )}
          
          {totalModules && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>
                <strong>Modules:</strong> {coreModules} kern modules
                {optionalModules > 0 && ` + ${optionalModules} optioneel`}
              </span>
            </div>
          )}

          {accreditation && (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              <span><strong>Accreditatie:</strong> {accreditation.points} punten ({accreditation.provider})</span>
            </div>
          )}
        </div>

        {/* Price and CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          {showPrice && (
            <div>
              <div className="text-2xl font-semibold text-purple-800">
                €{(price * 1.21).toFixed(2).replace('.', ',')}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                €{price.toFixed(2).replace('.', ',')} excl. BTW
              </div>
            </div>
          )}
          
          <Link
            to={buttonLink}
            className={`ml-auto px-6 py-2.5 rounded-md font-semibold transition-all ${
              comingSoon
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : isPurchased
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-purple-500 text-white hover:bg-purple-600'
            }`}
            onClick={(e) => comingSoon && e.preventDefault()}
          >
            {buttonText}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LessonCard;